import styled from 'styled-components';

// Badge variants and styles
export const StyledBadge = styled.span<{ 
  $variant: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'; 
  $size: 'sm' | 'md' | 'lg' 
}>`
  display: inline-flex;
  align-items: center;
  border-radius: var(--radius-full);
  font-weight: var(--font-weight-medium);
  transition: var(--transition-fast);
  white-space: nowrap;

  /* Size variants */
  padding: ${({ $size }) => {
    switch ($size) {
      case 'sm':
        return 'var(--spacing-1) var(--spacing-2)';
      case 'lg':
        return 'var(--spacing-2) var(--spacing-4)';
      default:
        return 'var(--spacing-1) var(--spacing-3)';
    }
  }};

  font-size: ${({ $size }) => {
    switch ($size) {
      case 'sm':
        return '0.75rem';
      case 'lg':
        return 'var(--font-size-14)';
      default:
        return 'var(--font-size-14)';
    }
  }};

  line-height: ${({ $size }) => {
    switch ($size) {
      case 'sm':
        return '1rem';
      case 'lg':
        return 'var(--line-height-21)';
      default:
        return 'var(--line-height-18)';
    }
  }};

  /* Color variants */
  ${({ $variant }) => {
    switch ($variant) {
      case 'primary':
        return `
          background-color: var(--color-primary-100);
          color: var(--color-primary-700);
        `;
      case 'secondary':
        return `
          background-color: var(--color-gray-100);
          color: var(--color-gray-600);
        `;
      case 'success':
        return `
          background-color: #dcfce7;
          color: var(--color-success);
        `;
      case 'warning':
        return `
          background-color: #fef3c7;
          color: var(--color-policy-orange);
        `;
      case 'error':
        return `
          background-color: #fee2e2;
          color: var(--color-error);
        `;
      case 'info':
        return `
          background-color: var(--color-primary-50);
          color: var(--color-primary-600);
        `;
      default:
        return `
          background-color: var(--color-gray-100);
          color: var(--color-gray-700);
        `;
    }
  }}
`;

// Circular Score Badge
export const StyledScoreBadge = styled.div<{ $score: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-full);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-14);
  color: var(--color-white);
  
  background-color: ${({ $score }) => {
    if ($score >= 80) return 'var(--color-success)';
    if ($score >= 60) return 'var(--color-policy-orange)';
    if ($score >= 40) return '#f59e0b';
    return 'var(--color-error)';
  }};
  
  border: 3px solid ${({ $score }) => {
    if ($score >= 80) return '#dcfce7';
    if ($score >= 60) return '#fef3c7';
    if ($score >= 40) return '#fed7aa';
    return '#fee2e2';
  }};
`;

// Helper function to get color based on score
const getScoreColor = (score: number) => {
  if (score >= 80) return 'var(--color-success)';
  if (score >= 60) return 'var(--color-policy-orange)';
  if (score >= 40) return '#f59e0b';
  return 'var(--color-error)';
};

// Concentric Circle Styles for ESG Score
export const StyledOuterCircle = styled.div<{ $score: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: var(--radius-full);
  background-color: ${({ $score }) => getScoreColor($score)};
  padding: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all var(--transition-fast);
`;

export const StyledMiddleCircle = styled.div<{ $score: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: var(--radius-full);
  background-color: ${({ $score }) => getScoreColor($score)};
  padding: 4px;
  transition: all var(--transition-fast);
`;

export const StyledInnerCircle = styled.div<{ $score: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: var(--radius-full);
  background-color: ${({ $score }) => getScoreColor($score)};
  color: var(--color-white);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-14);
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
  transition: all var(--transition-fast);
`;

// Action Button Badge
export const StyledActionBadge = styled.button<{ 
  $action: 'add_benefits' | 'payment_pending' | 'unlock' | 'boost' | 'publish' | 'fill_out' 
}>`
  padding: var(--spacing-2) var(--spacing-3);
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-size-14);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: var(--transition-fast);
  text-transform: capitalize;

  ${({ $action }) => {
    switch ($action) {
      case 'add_benefits':
        return `
          background-color: var(--color-primary-600);
          color: var(--color-white);
          
          &:hover {
            background-color: var(--color-primary-700);
          }
        `;
      case 'payment_pending':
        return `
          background-color: var(--color-policy-orange);
          color: var(--color-white);
          
          &:hover {
            background-color: #ea580c;
          }
        `;
      case 'unlock':
        return `
          background-color: var(--color-policy-orange);
          color: var(--color-white);
          
          &:hover {
            background-color: #ea580c;
          }
        `;
      case 'boost':
        return `
          background-color: var(--color-success);
          color: var(--color-white);
          
          &:hover {
            background-color: #047857;
          }
        `;
      case 'publish':
        return `
          background-color: var(--color-gray-600);
          color: var(--color-white);
          
          &:hover {
            background-color: var(--color-gray-700);
          }
        `;
      case 'fill_out':
        return `
          background-color: var(--color-primary-600);
          color: var(--color-white);
          
          &:hover {
            background-color: var(--color-primary-700);
          }
        `;
      default:
        return `
          background-color: var(--color-gray-200);
          color: var(--color-gray-700);
          
          &:hover {
            background-color: var(--color-gray-300);
          }
        `;
    }
  }}

  &:focus {
    outline: 2px solid var(--color-primary-500);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`; 